import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import {
  ACADEMY_WEEK_COUNT, academyWeekIndex, academyOpenUpto, academyProgress, academyNextOpenAt,
} from "../lib/academyLessons";

// 레이블리 아카데미 진행 상태 — 서버 스위치(academy_state RPC) + 내 이수 행(work_lesson_completion).
// 기획 docs/ops/labely-academy-plan.md §5 · §7. 실패는 fail-closed(꺼짐) — 페이지·홈 카드·메뉴가 조용히 사라진다.
// preview: ?preview=1 — RPC 가 없거나 꺼져 있어도 「전부 공개 · 오늘 시작」으로 화면을 보여 준다(테스트 사이트에서 DB 전에 확인).
//   이수 저장은 DB 제약이 없으면 실패하고 경고만 남긴다.

const OFF = { enabled: false, startAt: null, mode: "drip", weekIndex: -1, openUpto: -1 };

function normalizeState(raw, { preview = false, now = new Date() } = {}) {
  if (!raw || typeof raw !== "object") {
    if (!preview) return OFF;
    return { enabled: true, startAt: now.toISOString(), mode: "open", weekIndex: 0, openUpto: ACADEMY_WEEK_COUNT - 1, preview: true };
  }
  const enabled = Boolean(raw.enabled) || preview;
  const mode = raw.open_mode === "open" || (preview && !raw.enabled) ? "open" : "drip";
  const startAt = raw.start_at || (preview ? now.toISOString() : null);
  const serverNow = raw.server_now ? new Date(raw.server_now) : now;
  const weekIndex = typeof raw.week_index === "number" ? raw.week_index : academyWeekIndex(serverNow, startAt);
  const openUpto = typeof raw.open_upto === "number" && !preview ? raw.open_upto : academyOpenUpto({ mode, weekIndex });
  return { enabled, startAt, mode, weekIndex, openUpto, preview: preview && !raw.enabled };
}

export { normalizeState as normalizeAcademyState };

export default function useAcademyProgress(email, { enabled = true, preview = false } = {}) {
  const [loading, setLoading] = useState(Boolean(enabled));
  const [state, setState] = useState(OFF);
  const [completions, setCompletions] = useState([]);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    if (!enabled) { setLoading(false); setState(OFF); setCompletions([]); return undefined; }
    setLoading(true);
    (async () => {
      let raw = null;
      try {
        const { data, error } = await supabase.rpc("academy_state");
        if (!error) raw = data;
        else if (!preview) console.warn("[academy] academy_state rpc failed", error);
      } catch (e) {
        if (!preview) console.warn("[academy] academy_state rpc threw", e);
      }
      const next = normalizeState(raw, { preview });
      let rows = [];
      if (next.enabled && email) {
        try {
          const { data, error } = await supabase
            .from("work_lesson_completion")
            .select("lesson_key, lesson_version, trigger, completed_at")
            .eq("user_email", email)
            .like("lesson_key", "academy_%");
          if (!error && Array.isArray(data)) rows = data;
        } catch (e) {
          console.warn("[academy] completions fetch threw", e);
        }
      }
      if (!alive) return;
      setState(next);
      setCompletions(rows);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [email, enabled, preview, tick]);

  const progress = useMemo(
    () => academyProgress({ completions, openUpto: state.openUpto, mode: state.mode }),
    [completions, state.openUpto, state.mode],
  );
  const nextOpenAt = useMemo(
    () => (state.mode === "drip" ? academyNextOpenAt({ startAt: state.startAt, openUpto: state.openUpto }) : null),
    [state.mode, state.startAt, state.openUpto],
  );
  const lastCompletedAt = useMemo(() => {
    const ts = completions.map((r) => (r && r.completed_at ? new Date(r.completed_at).getTime() : 0)).filter((n) => n > 0);
    return ts.length ? new Date(Math.max(...ts)) : null;
  }, [completions]);

  return { loading, state, completions, progress, nextOpenAt, lastCompletedAt, refresh };
}
